import { Upload, UploadFile } from "antd";
import { makeAutoObservable } from "mobx";
// eslint-disable-next-line import/no-extraneous-dependencies
import { RcFile, UploadRequestOption } from "rc-upload/lib/interface";
import { KeyedImgFrame } from "src/business/attribute/attrEdits/AttrExternalImages/KeyedImgFrame";
import { getErrorMessage, onError } from "src/common/onError";
import { uploadFile } from "src/common/upload/apiUpload";
import { urlForDownload } from "src/common/upload/urlForDownload";
import { ZMdmFileEd } from "src/types/ZMdmFile";

import { checkFileSize } from "./checkFileSize";
import { filterClipboardData } from "./filterClipboardData";

type MdmUploadStoreParams = {
  isImage: boolean;
  maxCount?: number;
  maxSize?: number;
  accept?: string;
  onChange?(list: ZMdmFileEd[]): void;
};

export class MdmUploadStore {
  constructor(public readonly params: MdmUploadStoreParams) {
    makeAutoObservable(this);
  }

  get isImage() {
    return this.params.isImage;
  }

  get maxCount(): number {
    return this.params.maxCount ?? 1;
  }

  get maxSize(): number | undefined {
    return this.params.maxSize;
  }

  get accept() {
    return this.params.accept;
  }

  extFiles: ZMdmFileEd[] = [];

  setExtFiles(list: ZMdmFileEd[]) {
    const { filesDict } = this;
    // кеширование происходит тут, т.к. внутри get нежелательно менять состояние стора
    list.forEach(({ id, name }) => {
      const intId = this.getIntId(id);
      if (!filesDict[intId]) {
        filesDict[intId] = {
          uid: id,
          name,
          url: urlForDownload(id, this.isImage),
          thumbUrl: this.isImage ? urlForDownload(id, "small") : undefined,
          status: "done",
        };
      }
    });
    this.extFiles = list;
  }

  get intFiles(): UploadFile[] {
    const { filesDict } = this;
    return this.extFiles
      .map(({ id }) => {
        const intId = this.getIntId(id);
        return filesDict[intId];
      })
      .filter((it) => !!it);
  }

  setIntFiles(newList: UploadFile[]) {
    const { filesDict } = this;
    newList.forEach((it) => {
      filesDict[it.uid] = it;
    });
    this.params.onChange?.(
      newList.map(({ uid, name, size, type, status, error }) => {
        const extId = this.redirects.find(({ int }) => int === uid)?.ext ?? uid;
        return {
          id: extId,
          name,
          size,
          mime: type,
          errorMsg: statusToError(status, error),
        } satisfies ZMdmFileEd;
      }),
    );
  }

  get frames(): KeyedImgFrame[] {
    return this.intFiles.map(
      ({ uid, name, url, thumbUrl, status }) =>
        ({
          key: uid,
          label: name,
          url: url ?? "",
          thumbUrl,
          downloadUrl: status === "done" && url ? url : undefined,
        }) satisfies KeyedImgFrame,
    );
  }

  filesDict: Record<string, UploadFile> = {};

  redirects: Redirect[] = [];

  getIntId(extId: string): string {
    return this.redirects.find(({ ext }) => ext === extId)?.int ?? extId;
  }

  getExtId(intId: string): string {
    return this.redirects.find(({ int }) => int === intId)?.ext ?? intId;
  }

  disabled = false;

  setDisabled(newState: boolean) {
    this.disabled = newState;
  }

  modalOpen = false;

  setModalOpen(newState: boolean) {
    this.modalOpen = newState;
  }

  remove(index: number) {
    const newList = [...this.intFiles];
    newList.splice(index, 1);
    this.setIntFiles(newList);
  }

  removeByUid(uidToRemove: string) {
    const index = this.intFiles.findIndex(({ uid }) => uid === uidToRemove);
    if (index >= 0) {
      this.remove(index);
    }
  }

  async upload(file: Blob | RcFile, oldId: string) {
    const { path } = await uploadFile(file, this.isImage);
    this.redirects.push({
      int: oldId,
      ext: path,
    });
    return path;
  }

  get antUpload() {
    return async ({
      file,
      onSuccess,
      onError: onUploadError,
    }: UploadRequestOption) => {
      try {
        if (typeof file === "string")
          throw Error("Can't load file from string");
        // @ts-ignore
        const oldId = file.uid;
        await this.upload(file, oldId);
        onSuccess?.(file);
      } catch (error) {
        onUploadError?.(error);
        onError(error);
      }
    };
  }

  get beforeUpload() {
    return async (file: UploadFile) => {
      // Данная функция вызывается отдельно для каждого выбранного файла. (Хотя список один и тот же)
      try {
        this.filesDict[file.uid] = file;
        checkFileSize(file, this.maxSize);
        return true;
      } catch (error) {
        onError(error);
        return Upload.LIST_IGNORE;
      }
    };
  }

  lastPasteId = 1;

  generatePasteId() {
    return `paste-${this.lastPasteId++}`;
  }

  async onPaste(event: ClipboardEvent) {
    const files = filterClipboardData(
      Array.from(event.clipboardData?.items || [])
        .map((item) => item.getAsFile())
        .filter(Boolean) as File[],
      this.params.maxCount,
      this.intFiles,
      this.maxSize,
      this.accept,
    );

    const loadingFiles: UploadFile[] = files.map((file) => ({
      uid: this.generatePasteId(),
      name: file.name,
      status: "uploading",
      size: file.size,
      mime: file.type,
    }));
    loadingFiles.forEach((file) => {
      this.filesDict[file.name] = file;
    });

    this.setIntFiles([...this.intFiles, ...loadingFiles]);

    files.forEach(async (file, i) => {
      const info = loadingFiles[i];
      if (info) {
        try {
          const extId = await this.upload(file, info.uid);
          info.status = "done";
          info.url = urlForDownload(extId, this.isImage);
          info.thumbUrl = this.isImage
            ? urlForDownload(extId, "small")
            : undefined;
        } catch (error) {
          info.error = error;
          info.status = "error";
          onError(error);
        }
        this.setIntFiles(
          this.intFiles.map((it) => (it.uid === info.uid ? info : it)),
        );
      }
    });
  }
}

export type TFilesDict = Record<string, UploadFile>;

type Redirect = {
  int: string;
  ext: string;
};

const statusToError = (
  status: UploadFile["status"],
  error: UploadFile["error"],
): string | undefined => {
  if (status === "uploading") return "Загрузка не завершена";
  if (status === "error") {
    if (error instanceof Error) {
      const { message } = getErrorMessage(error);
      if (typeof message === "string") return message;
    }
    return "Ошибка загрузки";
  }
  return undefined;
};
